import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from "react-router-dom";
import LandingPage from "./pages/LandingPage";
import StartPage from "./pages/StartPage";
import BetaChallengePage from "./pages/BetaChallengePage";
import CheckoutReturnPage from "./pages/CheckoutReturnPage";
import HomePage from "./pages/HomePage";
import PreLaunchPage from "./pages/PreLaunchPage";
import AboutPage from "./pages/AboutPage";
import FounderStoryPage from "./pages/FounderStoryPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PaydayRelayMethodPage from "./pages/PaydayRelayMethodPage";
import ComparePage from "./pages/ComparePage";
import FeaturesPage from "./pages/FeaturesPage";
import CaddyCalculatorPage from "./pages/CaddyCalculatorPage";
import SheetSetupPage from "./pages/SheetSetupPage";
import SupportPage from "./pages/SupportPage";
import LoginPage from "./pages/LoginPage";
import AuthVerifyPage from "./pages/AuthVerifyPage";
import ThankYouPage from "./pages/ThankYouPage";
import CaddyPage from "./pages/CaddyPage";
import QuarterlyResetPage from "./pages/QuarterlyResetPage";
import RenewalPage from "./pages/RenewalPage";
import JournalPage from "./pages/JournalPage";
import SettingsPage from "./pages/SettingsPage";
import MonthlyWrapPage from "./pages/MonthlyWrapPage";
import ScoreboardPage from "./pages/ScoreboardPage";
import ResourceLibraryPage from "./pages/ResourceLibraryPage";
import EntryHistoryPage from "./pages/EntryHistoryPage";
import TimeOffPage from "./pages/TimeOffPage";
import { SeasonGateRedirect } from "./components/SeasonGateRedirect";
import CaddyMorePage from "./pages/CaddyMorePage";
import WhatsNewPage from "./pages/WhatsNewPage";
import BetaSurveyPage from "./pages/BetaSurveyPage";
import BetaFeedbackSurveyPage from "./pages/BetaFeedbackSurveyPage";
import PartnershipsPage from './pages/PartnershipsPage';
import PressKitPage from './pages/PressKitPage';
import QuoteApprovalPage from "./pages/QuoteApprovalPage";
import ReviewerGuidePage from "./pages/ReviewerGuidePage";
import AdminControlRoomRedirect from "./pages/AdminControlRoomRedirect";
import ReviewerChangesPage from "./pages/ReviewerChangesPage";
import { SheetReconnectBanner } from "./components/SheetReconnectBanner";
import ReviewerFeedbackPage from "./pages/ReviewerFeedbackPage";
import LearnPage from "./pages/LearnPage";
import EmailPreferencesPage from "./pages/EmailPreferencesPage";
import PrivacyEye from "./components/PrivacyEye";
import BetaOpenModal from "./components/BetaOpenModal";
import { openBetaModal } from "./lib/beta";
import CoachBubble from "./components/CoachBubble";
import { AuthCallback } from "./components/AuthCallback";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "./components/ui/sonner";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Land every page at the top. Hash links (e.g. /#season-pass) keep their anchor,
// and we disable the browser's own scroll restoration so it can't drop you
// mid-page after our scroll runs.
// Single app-wide beta modal, opened from anywhere via openBetaModal().
function GlobalBetaModal() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("payday:open-beta", h);
    return () => window.removeEventListener("payday:open-beta", h);
  }, []);
  return <BetaOpenModal isOpen={open} onClose={() => setOpen(false)} />;
}

// Legacy beta entry points now pop the form instead of a landing page.
function OpenBetaRoute() {
  useEffect(() => { openBetaModal(); }, []);
  return <Navigate to="/" replace />;
}

// Keeps ?query when moving a retired slug to its new home.
function RedirectKeepSearch({ to }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      // Target may not be in the DOM yet on a client-side navigation; wait a tick,
      // then scroll it into view (H2s carry scroll-mt for the sticky nav).
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
        else window.scrollTo(0, 0);
      }, 60);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
    // Images/fonts settling can re-trigger a restore, pin it once more next frame.
    const raf = requestAnimationFrame(() => window.scrollTo(0, 0));
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}

// Emergent Google auth returns to `{origin}/dashboard#session_id=...`, exchange it before routing.
function AuthGate({ children }) {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return children;
}

// Pre-launch mode: while enabled the public root shows the branded launch page.
// The real marketing homepage stays reachable at a private, unlisted slug for
// testing. Flip off by setting REACT_APP_PRELAUNCH_MODE to anything but "true".
const PRELAUNCH = process.env.REACT_APP_PRELAUNCH_MODE === "true";
const PREVIEW_HOME_SLUG = "/beta";

// Global chrome (coach bubble) is hidden on the public
// pre-launch page so it never exposes the in-app product.
function GlobalChrome() {
  const { pathname } = useLocation();
  const onPrelaunch = PRELAUNCH && pathname === "/";
  if (onPrelaunch) return <CookieConsent />;
  return (
    <>
      <CoachBubble />
      <CookieConsent />
    </>
  );
}

/** Toast surfaces follow the app theme (day / night). */
const ThemedToaster = () => {
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") || "light");
  useEffect(() => {
    const onChange = (e) => setTheme(e.detail || "light");
    window.addEventListener("pr-theme-change", onChange);
    return () => window.removeEventListener("pr-theme-change", onChange);
  }, []);
  return <Toaster position="bottom-right" richColors theme={theme} />;
};


function App() {
  return (
    <div className="App">
      <ThemedToaster />
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <AuthGate>
          <SheetReconnectBanner />
          <PrivacyEye />
          <Routes>
            <Route path="/" element={PRELAUNCH ? <PreLaunchPage /> : <HomePage />} />
            {PRELAUNCH && <Route path={PREVIEW_HOME_SLUG} element={<HomePage />} />}
            <Route path="/prelaunch" element={<PreLaunchPage />} />
            <Route path="/beta-challenge" element={<BetaChallengePage />} />
            <Route path="/learn" element={<LearnPage />} />
            <Route path="/learn/:slug" element={<LearnPage />} />
            <Route path="/email-preferences" element={<EmailPreferencesPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/founder-story" element={<FounderStoryPage />} />
            <Route path="/income-map" element={<LandingPage />} />
            <Route path="/start/free-sprint" element={<StartPage kind="free_sprint" />} />
            <Route path="/start/season-pass" element={<StartPage kind="season_pass" />} />
            {/* Retired funnel routes → new unified funnel */}
            <Route path="/start/income-map" element={<Navigate to="/start/free-sprint" replace />} />
            <Route path="/start/relay-sheet" element={<Navigate to="/start/season-pass" replace />} />
            <Route path="/start/payday-relay-core" element={<Navigate to="/start/season-pass" replace />} />
            <Route path="/start/4-relay-challenge" element={<Navigate to="/start/free-sprint" replace />} />
            <Route path="/checkout/return" element={<CheckoutReturnPage />} />
            <Route path="/payday-relay" element={<Navigate to="/#season-pass" replace />} />
            <Route path="/method" element={<PaydayRelayMethodPage />} />
            <Route path="/payday-relay-method" element={<PaydayRelayMethodPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/press" element={<PressKitPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            {/* One Relay Calculator, inside the app (sign-in required). Old public slugs fold into it. */}
            <Route path="/app/calculator" element={<CaddyCalculatorPage />} />
            <Route path="/relay-calculator" element={<RedirectKeepSearch to="/app/calculator" />} />
            <Route path="/caddy/calculator" element={<RedirectKeepSearch to="/app/calculator" />} />
            <Route path="/sheet-setup" element={<SheetSetupPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/verify" element={<AuthVerifyPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/pro-pit-crew" element={<Navigate to="/" replace />} />
            <Route path="/app" element={<CaddyPage />} />
            <Route path="/app/more" element={<SeasonGateRedirect><CaddyMorePage /></SeasonGateRedirect>} />
            <Route path="/dashboard" element={<CaddyPage />} />
            <Route path="/app/reset" element={<SeasonGateRedirect><QuarterlyResetPage /></SeasonGateRedirect>} />
            <Route path="/app/renew" element={<SeasonGateRedirect><RenewalPage /></SeasonGateRedirect>} />
            <Route path="/app/journal" element={<SeasonGateRedirect><JournalPage /></SeasonGateRedirect>} />
            <Route path="/app/settings" element={<SettingsPage />} />
            <Route path="/app/allocations" element={<Navigate to="/app/more" replace />} />
            <Route path="/app/monthly" element={<SeasonGateRedirect><MonthlyWrapPage /></SeasonGateRedirect>} />
            <Route path="/app/scoreboard" element={<SeasonGateRedirect><ScoreboardPage /></SeasonGateRedirect>} />
            <Route path="/app/resources" element={<SeasonGateRedirect><ResourceLibraryPage /></SeasonGateRedirect>} />
            <Route path="/app/history" element={<EntryHistoryPage />} />
            <Route path="/app/timeoff" element={<SeasonGateRedirect><TimeOffPage /></SeasonGateRedirect>} />
            <Route path="/app/beta-feedback" element={<BetaFeedbackSurveyPage />} />
            {/* Legacy /caddy/* URLs, redirected to /app/* so old bookmarks and emails keep working. */}
            <Route path="/caddy" element={<RedirectKeepSearch to="/app" />} />
            <Route path="/caddy/more" element={<RedirectKeepSearch to="/app/more" />} />
            <Route path="/caddy/reset" element={<RedirectKeepSearch to="/app/reset" />} />
            <Route path="/caddy/renew" element={<RedirectKeepSearch to="/app/renew" />} />
            <Route path="/caddy/journal" element={<RedirectKeepSearch to="/app/journal" />} />
            <Route path="/caddy/allocations" element={<RedirectKeepSearch to="/app/allocations" />} />
            <Route path="/caddy/monthly" element={<RedirectKeepSearch to="/app/monthly" />} />
            <Route path="/caddy/scoreboard" element={<RedirectKeepSearch to="/app/scoreboard" />} />
            <Route path="/caddy/resources" element={<RedirectKeepSearch to="/app/resources" />} />
            <Route path="/caddy/timeoff" element={<RedirectKeepSearch to="/app/timeoff" />} />
            <Route path="/caddy/beta-feedback" element={<RedirectKeepSearch to="/app/beta-feedback" />} />
            <Route path="/beta-survey" element={<BetaSurveyPage />} />
            <Route path="/whats-new" element={<WhatsNewPage />} />
            <Route path="/quote-approval" element={<QuoteApprovalPage />} />
            <Route path="/reviewer-guide" element={<ReviewerGuidePage />} />
            {/* Private admin front doors, hand off to the admin-gated Control Room page */}
            <Route path="/admin/control-room" element={<AdminControlRoomRedirect />} />
            <Route path="/admin/links" element={<AdminControlRoomRedirect />} />
            <Route path="/reviewer-changes" element={<ReviewerChangesPage />} />
            <Route path="/reviewer-feedback" element={<ReviewerFeedbackPage />} />
            <Route path="/partnerships" element={<PartnershipsPage />} />
            <Route path="/join-the-beta" element={<OpenBetaRoute />} />
            <Route path="/beta-signup" element={<OpenBetaRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AuthGate>
          <GlobalChrome />
          <GlobalBetaModal />
        </ErrorBoundary>
      </BrowserRouter>
    </div>
  );
}

export default App;
